import { create } from 'zustand';
import {
  getUserDirectory,
  getUserStats,
  getDeactivationLogs,
  getUserById,
  createUser,
  updateUser,
  deactivateUser,
  reactivateUser,
  bulkImportUsers,
  getDepartments,
  User,
  UserDetail,
  UserStats,
  DeactivationLog,
  Pagination,
  CreateUserDTO,
  UpdateUserDTO,
  BulkImportUser,
} from '@/services/PagesServices/UsersService';

interface UsersState {
  // Data states
  users: User[];
  stats: UserStats | null;
  deactivationLogs: DeactivationLog[];
  departments: string[];
  selectedUser: UserDetail | null;

  // Pagination states
  usersPagination: Pagination | null;
  logsPagination: Pagination | null;

  // Filter states
  searchTerm: string;
  selectedDepartment: string;
  selectedStatus: string;
  selectedRole: string;
  currentPage: number;
  itemsPerPage: number;

  // Loading states
  loadingUsers: boolean;
  loadingStats: boolean;
  loadingLogs: boolean;
  loadingUser: boolean;
  creating: boolean;
  updating: boolean;
  deactivating: boolean;
  reactivating: boolean;
  importing: boolean;

  // Error states
  error: string | null;

  // Actions
  fetchUsers: (params?: {
    search?: string;
    status?: string;
    department?: string;
    role?: string;
    page?: number;
    limit?: number;
  }) => Promise<void>;
  fetchStats: () => Promise<void>;
  fetchDeactivationLogs: (page?: number, limit?: number) => Promise<void>;
  fetchUserById: (userId: string) => Promise<UserDetail | null>;
  fetchDepartments: () => Promise<void>;
  createUser: (userData: CreateUserDTO) => Promise<{ success: boolean; message: string; data?: { userId: string; generatedPassword: string } }>;
  updateUser: (userId: string, userData: UpdateUserDTO) => Promise<{ success: boolean; message: string }>;
  deactivateUser: (userId: string, reason: string) => Promise<{ success: boolean; message: string }>;
  reactivateUser: (userId: string) => Promise<{ success: boolean; message: string }>;
  bulkImportUsers: (users: BulkImportUser[]) => Promise<{ success: boolean; message: string; data?: { success: number; failed: number } }>;

  // Filter actions
  setSearchTerm: (term: string) => void;
  setSelectedDepartment: (department: string) => void;
  setSelectedStatus: (status: string) => void;
  setSelectedRole: (role: string) => void;
  setCurrentPage: (page: number) => void;
  setItemsPerPage: (limit: number) => void;
  setSelectedUser: (user: UserDetail | null) => void;
  clearFilters: () => void;
}

const useUsersStore = create<UsersState>((set, get) => ({
  // Initial states
  users: [],
  stats: null,
  deactivationLogs: [],
  departments: [],
  selectedUser: null,

  usersPagination: null,
  logsPagination: null,

  searchTerm: '',
  selectedDepartment: 'All Department',
  selectedStatus: 'All Status',
  selectedRole: 'All Roles',
  currentPage: 1,
  itemsPerPage: 10,

  loadingUsers: false,
  loadingStats: false,
  loadingLogs: false,
  loadingUser: false,
  creating: false,
  updating: false,
  deactivating: false,
  reactivating: false,
  importing: false,

  error: null,

  // Fetch users with filters and pagination
  fetchUsers: async (params) => {
    const state = get();
    const queryParams = {
      search: params?.search ?? state.searchTerm,
      status: params?.status ?? state.selectedStatus,
      department: params?.department ?? state.selectedDepartment,
      role: params?.role ?? state.selectedRole,
      page: params?.page ?? state.currentPage,
      limit: params?.limit ?? state.itemsPerPage,
    };

    set({ loadingUsers: true, error: null });

    try {
      const response = await getUserDirectory(queryParams);

      if (!response.success) {
        throw new Error(response.error?.message ?? 'Failed to fetch users');
      }

      set({
        users: response.data || [],
        usersPagination: response.pagination || null,
        loadingUsers: false,
      });
    } catch (error: any) {
      console.error('UsersStore: Error in fetchUsers:', error);
      set({
        error: error.message ?? 'Something went wrong',
        loadingUsers: false,
      });
    }
  },

  // Fetch user statistics
  fetchStats: async () => {
    set({ loadingStats: true });

    try {
      const response = await getUserStats();

      if (!response.success) {
        throw new Error(response.error?.message ?? 'Failed to fetch stats');
      }

      set({
        stats: response.data || null,
        loadingStats: false,
      });
    } catch (error: any) {
      console.error('UsersStore: Error in fetchStats:', error);
      set({ loadingStats: false });
    }
  },

  // Fetch deactivation logs
  fetchDeactivationLogs: async (page = 1, limit = 10) => {
    set({ loadingLogs: true });

    try {
      const response = await getDeactivationLogs(page, limit);

      if (!response.success) {
        throw new Error(response.error?.message ?? 'Failed to fetch logs');
      }

      set({
        deactivationLogs: response.data || [],
        logsPagination: response.pagination || null,
        loadingLogs: false,
      });
    } catch (error: any) {
      console.error('UsersStore: Error in fetchDeactivationLogs:', error);
      set({ loadingLogs: false });
    }
  },

  // Fetch single user by ID
  fetchUserById: async (userId) => {
    set({ loadingUser: true });

    try {
      const response = await getUserById(userId);

      if (!response.success || !response.data) {
        throw new Error(response.error?.message ?? 'User not found');
      }

      set({
        selectedUser: response.data,
        loadingUser: false,
      });

      return response.data;
    } catch (error: any) {
      console.error('UsersStore: Error in fetchUserById:', error);
      set({ loadingUser: false });
      return null;
    }
  },

  // Fetch departments for filter
  fetchDepartments: async () => {
    try {
      const response = await getDepartments();

      if (response.success && response.data) {
        set({ departments: response.data });
      }
    } catch (error: any) {
      console.error('UsersStore: Error in fetchDepartments:', error);
    }
  },

  // Create new user
  createUser: async (userData) => {
    set({ creating: true });

    try {
      const response = await createUser(userData);

      set({ creating: false });

      if (!response.success) {
        return {
          success: false,
          message: response.error?.message ?? 'Failed to create user',
        };
      }

      // Refresh users list
      get().fetchUsers();
      get().fetchStats();

      return {
        success: true,
        message: 'User created successfully',
        data: response.data,
      };
    } catch (error: any) {
      console.error('UsersStore: Error in createUser:', error);
      set({ creating: false });

      return {
        success: false,
        message: error.message ?? 'Failed to create user',
      };
    }
  },

  // Update user
  updateUser: async (userId, userData) => {
    set({ updating: true });

    try {
      const response = await updateUser(userId, userData);

      set({ updating: false });

      if (!response.success) {
        return {
          success: false,
          message: response.error?.message ?? 'Failed to update user',
        };
      }

      // Refresh users list
      get().fetchUsers();

      return {
        success: true,
        message: response.message ?? 'User updated successfully',
      };
    } catch (error: any) {
      console.error('UsersStore: Error in updateUser:', error);
      set({ updating: false });

      return {
        success: false,
        message: error.message ?? 'Failed to update user',
      };
    }
  },

  // Deactivate user
  deactivateUser: async (userId, reason) => {
    set({ deactivating: true });

    try {
      const response = await deactivateUser(userId, reason);

      set({ deactivating: false });

      if (!response.success) {
        return {
          success: false,
          message: response.error?.message ?? 'Failed to deactivate user',
        };
      }

      // Refresh data
      get().fetchUsers();
      get().fetchStats();
      get().fetchDeactivationLogs();

      return {
        success: true,
        message: response.message ?? 'User deactivated successfully',
      };
    } catch (error: any) {
      console.error('UsersStore: Error in deactivateUser:', error);
      set({ deactivating: false });

      return {
        success: false,
        message: error.message ?? 'Failed to deactivate user',
      };
    }
  },

  // Reactivate user
  reactivateUser: async (userId) => {
    set({ reactivating: true });

    try {
      const response = await reactivateUser(userId);

      set({ reactivating: false });

      if (!response.success) {
        return {
          success: false,
          message: response.error?.message ?? 'Failed to reactivate user',
        };
      }

      // Refresh data
      get().fetchUsers();
      get().fetchStats();
      get().fetchDeactivationLogs();

      return {
        success: true,
        message: response.message ?? 'User reactivated successfully',
      };
    } catch (error: any) {
      console.error('UsersStore: Error in reactivateUser:', error);
      set({ reactivating: false });

      return {
        success: false,
        message: error.message ?? 'Failed to reactivate user',
      };
    }
  },

  // Bulk import users
  bulkImportUsers: async (users) => {
    set({ importing: true });

    try {
      const response = await bulkImportUsers(users);

      set({ importing: false });

      if (!response.success) {
        return {
          success: false,
          message: response.error?.message ?? 'Failed to import users',
        };
      }

      // Refresh data
      get().fetchUsers();
      get().fetchStats();

      return {
        success: true,
        message: response.message ?? 'Users imported successfully',
        data: response.data,
      };
    } catch (error: any) {
      console.error('UsersStore: Error in bulkImportUsers:', error);
      set({ importing: false });

      return {
        success: false,
        message: error.message ?? 'Failed to import users',
      };
    }
  },

  // Filter actions
  setSearchTerm: (term) => set({ searchTerm: term }),
  setSelectedDepartment: (department) => set({ selectedDepartment: department }),
  setSelectedStatus: (status) => set({ selectedStatus: status }),
  setSelectedRole: (role) => set({ selectedRole: role }),
  setCurrentPage: (page) => set({ currentPage: page }),
  setItemsPerPage: (limit) => set({ itemsPerPage: limit }),
  setSelectedUser: (user) => set({ selectedUser: user }),

  clearFilters: () =>
    set({
      searchTerm: '',
      selectedDepartment: 'All Department',
      selectedStatus: 'All Status',
      selectedRole: 'All Roles',
      currentPage: 1,
    }),
}));

export default useUsersStore;
